import { useFormFieldContext } from '../hooks/UNSAFE_useFormFieldContext';
import { classNames } from '../utils/UNSAFE_classNames';
import { StyledComponentProps } from '../utils/UNSAFE_typeUtils';
import { useFormContext } from '../hooks/UNSAFE_useFormContext';
import { useComponentTheme } from '../hooks/UNSAFE_useComponentTheme';
import { CompactLabelAssistance } from '../UNSAFE_UserAssistance';

import { LabelStyles, LabelVariantOptions } from './themes/LabelStyles.css';
import { TextFieldRedwoodTheme } from '../UNSAFE_TextField/themes/redwood/TextFieldTheme';
import { LabelRedwoodTheme } from './themes/redwood/LabelTheme';

type StyleProps = Exclude<Props, 'children' | 'forId' | 'id'> & {
  hasValue?: boolean;
  isFocused?: boolean;
};

type StyledLabelProps = StyledComponentProps<'label', StyleProps>;

const StyledLabel = ({
  hasValue,
  readonly,
  disabled,
  isFocused,
  isRequiredShown,
  helpIconId,
  helpSourceLink,
  helpSourceText,
  userAssistanceDensity = 'reflow',
  variant = 'inside',
  parentComponentVariant = 'textField',
  ...props
}: StyledLabelProps) => {
  const { isFormLayout, isReadonly: isReadonlyForm, labelWrapping } = useFormContext();

  const isInside = variant === 'inside' || variant === 'insideError' || variant === 'insideWarning';
  const inTextField = parentComponentVariant === 'textField';

  // because variants have to match strings, we have to take any boolean props and convert
  // them to a string value, (ie prefixed with "is" or "non")
  const {
    classes,
    styles: { uaDensityCompactLabel, uaDensityCompactLabelStart, uaDensityCompactLabelTopInside }
  } = useComponentTheme<LabelVariantOptions, LabelStyles>(LabelRedwoodTheme, {
    labelWrapping,
    position: variant,
    inside: isInside ? 'isInside' : 'notInside',
    inEnabledForm: isFormLayout && !isReadonlyForm ? 'isInEnabledForm' : 'notInEnabledForm',
    readonly: readonly ? 'isReadonly' : 'notReadonly',
    focused: isFocused ? 'isFocused' : 'nonFocused',
    formLayout: isFormLayout ? 'isFormLayout' : 'nonFormLayout',
    readonlyForm: isReadonlyForm ? 'isReadonlyForm' : 'notReadonlyForm',
    value: hasValue ? 'hasValue' : 'noValue',
    valueOrFocus: hasValue || isFocused ? 'hasValueOrFocus' : 'noValueOrFocus',
    animatedWhenInside: inTextField ? 'isAnimated' : 'notAnimated',
    disabled: inTextField && disabled ? 'isDisabled' : 'nonDisabled',
    userAssistanceDensity
  });

  const { baseTheme: textFieldTheme } = useComponentTheme(TextFieldRedwoodTheme);

  // TODO: We need to decide whether to standardize on 'class' or 'className'.  Otherwise, we'll
  // run into issues destructuring style interpolations, for example having to concatenate both.

  const compactLabelStyles = classNames([
    uaDensityCompactLabel,
    variant === 'start' ? uaDensityCompactLabelStart : uaDensityCompactLabelTopInside
  ]);
  const compactUADLabel = <label {...props} class={compactLabelStyles} />;

  return userAssistanceDensity === 'compact' ? (
    <div class={classNames([classes, textFieldTheme])}>
      {variant !== 'start' && compactUADLabel}
      <CompactLabelAssistance
        isRequiredShown={isRequiredShown}
        helpIconId={helpIconId}
        helpSourceLink={helpSourceLink}
        helpSourceText={helpSourceText}
        labelEdge={variant === 'top' || variant === 'start' ? variant : 'inside'}
      />
      {variant === 'start' && compactUADLabel}
    </div>
  ) : (
    <label {...props} class={classNames([classes, textFieldTheme])} />
  );
};

type Props = {
  /**
   * Specifies how messages and assistive text are shown.
   */
  userAssistanceDensity?: 'reflow' | 'efficient' | 'compact';
  /**
   * Id to use for the help icon in compact user assistance density.
   */
  helpIconId?: string;
  /**
   * Help source link for compact user assistance density.
   */
  helpSourceLink?: string;
  /**
   * Help source text for compact user assistance density.
   */
  helpSourceText?: string;
  /**
   * Specifies whether to show an indicator on screen that a value is required, for example
   * before the user has committed a value.
   */
  isRequiredShown?: boolean;
  /**
   * The text to be used for the label
   */
  children?: string;
  /**
   * The id to set on the label
   */
  id?: string;
  /**
   * The id of the form control for which this label will be used
   */
  forId?: string;
  /**
   * Determines how the label is going to be styled
   */
  variant?: 'inside' | 'start' | 'top' | 'insideError' | 'insideWarning';
  /**
   * Determines how the label is going to be styled in based on parent component.
   */
  parentComponentVariant?: 'textField' | 'radioGroup';
};

export const Label = ({ forId, ...props }: Props) => {
  const { hasValue, isDisabled, isFocused, isReadonly } = useFormFieldContext();
  return (
    <StyledLabel
      for={forId}
      hasValue={hasValue}
      disabled={isDisabled}
      isFocused={isFocused}
      readonly={isReadonly}
      {...props}
    />
  );
};
